import type { RESOURCE_STATUSES_TYPE } from '@duke-hq/shared-constants/resourceStatuses';
import type { StudentCourseEnrolmentStatus } from '@duke-hq/shared-constants/studentEnrolment';
import type { UnitPlacementStatus } from '@duke-hq/shared-constants/workPlacementStatus';
import type React from 'react';

export interface CourseItemsStatusAccordionLabels {
  semester: (n: number) => string;
  lln: string;
  skillSetUnits: string;
  enrolled: string;
  notEnrolled: string;
  unit: string;
  releaseDate: string;
  status: string;
}

export const defaultLabels: CourseItemsStatusAccordionLabels = {
  semester: (n: number) => `Semester ${n}`,
  lln: 'Language, Literacy, and Numeracy',
  skillSetUnits: 'Skill set units',
  enrolled: 'Enrolled',
  notEnrolled: 'Not Enrolled',
  unit: 'Unit',
  releaseDate: 'Release date',
  status: 'Status',
};

export type UnitActionsProps = {
  enrolmentId: number;
  unitOfCompetencyId: string;
  previousItemDueDate: Date | string | null;
  enrolmentStatus: StudentCourseEnrolmentStatus;
  unitScheduleOverallStatus: RESOURCE_STATUSES_TYPE;
  isAccredited: boolean;
  isLLN: boolean;
};

export interface ProcessedUnitScheduleItem {
  id: string;
  item_id: string;
  item_type: string;
  unit_of_competency_id: string;
  position: number;
  semester: number;
  scheduled_start_date: Date;
  started_at: Date | null;
  is_complete: boolean;
  admin_course_schedule_week: number;
  itemTitle: string | undefined;
  resourceStatus: RESOURCE_STATUSES_TYPE;
  submissionId: number | null;
}

export interface ProcessedUnit {
  id: string;
  semester: number;
  semesterReleaseDate: Date;
  position: number;
  unitTitle: string | undefined;
  unitCode: string;
  overallStatus: RESOURCE_STATUSES_TYPE;
  scheduleItemDetails: ProcessedUnitScheduleItem[];
  isLanguageLearningUnit: boolean;
  effectiveStartDate: Date;
  unitOfCompetencyId: string;
}

export type ProcessedUnits = Record<string, ProcessedUnit[]>;

export interface ScheduleAdjustment {
  id: string;
  item_id: string;
  item_type: string;
  scheduled_start_date: Date;
  updated_at: Date;
  adjustedByDrn: string | null;
  adjustedByDrnDisplayName: string | null;
}

interface CourseEnrolmentScheduleItem {
  id: string;
  item_id: string;
  item_type: string;
  unit_of_competency_id: string;
  scheduled_start_date: Date;
  scheduled_due_date: Date;
  started_at: Date | null;
  semester: number;
  position: number;
  is_complete: boolean;
  admin_course_schedule_week: number;
}

interface UnitOfCompetency {
  id: string;
  title: string | undefined;
  code: string;
  work_placement: boolean;
  resources: {
    id: string;
    item_id: string;
    item_type: string;
    status: string | undefined;
    title: string | undefined;
    submissionId: number | null;
  }[];
}

export interface CourseEnrolmentForAccordion {
  id: number;
  status: StudentCourseEnrolmentStatus;
  course?: { kind: string; accredited: boolean } | null;
  courseEnrolmentScheduleItems: CourseEnrolmentScheduleItem[];
  unitsOfCompetencyById: Record<string, UnitOfCompetency>;
  completedWorkPlacementUocIds: string[];
  workPlacementByUocId: Record<string, UnitPlacementStatus>;
  CourseEnrolmentScheduleAdjustments: ScheduleAdjustment[];
}

export interface CourseEnrolmentComponentProps {
  courseEnrolment: CourseEnrolmentForAccordion;
  processedUnits: ProcessedUnits;
  UnitActionsComponent?: React.ComponentType<UnitActionsProps>;
  labels?: Partial<CourseItemsStatusAccordionLabels>;
  /**
   * When provided, submitted assessment rows render as links to the returned
   * path (built from the submission id). Only wired up in the admin app.
   */
  getAssessmentLink?: (submissionId: number) => string;
}
